/**
 * GATE ON A PERMISSION, NEVER ON A GROUP NAME.
 *
 * The frontend's checks are for usability only: every act is authorised again
 * on the server. The permission catalogue is the backend's; the names below are
 * the ones core surfaces already gate on in the POC, and the list grows as each
 * module's OpenAPI arrives.
 *
 * INTERIM, 18-09-2026 — PharmOS API 1.0.0 states ROLES and not permissions, and
 * Rachel's answer is that permissions arrive with Settings (GAPS.md B5). Until
 * they do, `session.permissionsSupplied` is false and `can()` stops gating: it
 * still enforces the registered-pharmacist rule, which is a fact about the
 * person and not a grant, and it lets everything else through.
 *
 * That is deliberate and it is the lesser of two wrongs. Denying instead would
 * make every gated door dead with no way to open it, and the door would be lying
 * — it would read "you may not", when the truth is "nobody has said". The server
 * authorises every act regardless, which is why a usability gate may be off
 * without anything becoming reachable that should not be.
 *
 * WHEN B5 LANDS: fill `permissions` from the session read model, set
 * `permissionsSupplied` true, and delete the branch below. There is exactly one.
 */
import type { Session } from "./session";

export const PERMISSIONS = [
  "dashboard.view",
  "dispense.view",
  "dispense.override",
  "pos.operate",
  "pos.refund",
  "pos.dayend",
  "inventory.view",
  "inventory.manage",
  "inventory.adjust",
  "patients.view",
  "patients.edit",
  "reports.view",
  "financials.view",
  "register.sign",
  "admin.staff",
  "admin.access",
  "admin.company",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Acts that need the PERSON to be a declared registered pharmacist, whatever
 * their group grants. Being registered is a fact about a human being; a group
 * is a job title a pharmacy chose.
 */
export const NEEDS_REGISTRATION: ReadonlySet<Permission> = new Set(["register.sign", "dispense.override"]);

export function meetsRegistration(permission: Permission, registeredPharmacist: boolean): boolean {
  return !NEEDS_REGISTRATION.has(permission) || registeredPharmacist;
}

/** Signed in, not expired, granted, and registered where the act needs it. */
export function can(
  session: Session | null | undefined,
  permission: Permission,
  now: Date = new Date(),
): boolean {
  if (!session) return false;
  // Absent is not zero: no expiry stated means the server did not say, and the
  // server ends the session by answering 401 — not this clock.
  if (session.expiresAt !== null && new Date(session.expiresAt).getTime() <= now.getTime()) {
    return false;
  }
  if (!session.permissionsSupplied) return meetsRegistration(permission, session.registeredPharmacist);
  if (!session.permissions.includes(permission)) return false;
  return meetsRegistration(permission, session.registeredPharmacist);
}
